import {
  classifyPlanDeComSupport,
  decodePlanDeComSupportValue,
  extractPlanDeComSupportDate,
  isPlanDeComSupportRepeatable,
  PDC_WEB_KINDS,
} from './planDeComSupportUtils';
import { formatPlanDeComPeriode } from './planDeComBdcOptions';
import { MOIS_LABELS } from './planDeComWarnings';

const formatMoney = (v) => (v == null || v === '' ? '—' : `${Number(v).toLocaleString('fr-FR')} €`);

export const formatPlanDeComMoney = formatMoney;
export const PLAN_DE_COM_LEGACY_FORMAT_FIELDS = ['format_1', 'format_2', 'format_3'];
export const PLAN_DE_COM_LEGACY_FEE_FIELDS = ['frais_crea', 'frais_redac', 'frais_klub'];
export const PLAN_DE_COM_SUPPORT_FEE_FIELDS = {
  format_1: 'frais_crea',
  format_2: 'frais_redac',
  format_3: 'frais_klub',
};

export function getPlanDeComSupportFeeField(field) {
  return PLAN_DE_COM_SUPPORT_FEE_FIELDS[field] ?? null;
}

export function formatPlanDeComQuantityLabel(label, qty) {
  const text = String(label ?? '').trim();
  const count = Number(qty) || 0;
  if (!text || count <= 1) return text;
  return `${text} x${count}`;
}

export function splitPlanDeComQuantityLabel(label) {
  const text = String(label ?? '').trim();
  const match = text.match(/^(.*)\s+x(\d+)$/i);
  if (!match) return { label: text, qty: 1 };
  const base = match[1].trim();
  const qty = Number(match[2]) || 1;
  return { label: base || text, qty: qty > 1 ? qty : 1 };
}

function normalizeSupportItem(row, index, ligne) {
  const value = String(row?.value ?? row ?? '').trim();
  if (!value) return null;
  const field = `format_${index + 1}`;
  const decoded = decodePlanDeComSupportValue(value);
  const feeLabel = row?.frais ?? ligne?.[getPlanDeComSupportFeeField(field)] ?? '';
  const kind = classifyPlanDeComSupport(decoded.tarifLibelle || decoded.label);
  const dateLabel = PDC_WEB_KINDS.includes(kind) ? extractPlanDeComSupportDate(value) : null;
  return {
    field,
    index,
    feeField: getPlanDeComSupportFeeField(field),
    feeLabel,
    remises: ligne?.calcules?.support_remises?.[field] ?? [],
    value,
    label: decoded.label || value,
    tarifLibelle: decoded.tarifLibelle,
    slotKey: decoded.slotKey,
    kind,
    dateLabel,
    repeatable: isPlanDeComSupportRepeatable(value, decoded.slotKey),
  };
}

export function getPlanDeComLigneSupportItems(ligne) {
  if (Array.isArray(ligne?.supports) && ligne.supports.length > 0) {
    return ligne.supports
      .map((row, index) => normalizeSupportItem(row, index, ligne))
      .filter(Boolean);
  }

  return PLAN_DE_COM_LEGACY_FORMAT_FIELDS
    .map((field, index) => {
      const value = ligne?.[field];
      if (!value) return null;
      return normalizeSupportItem({
        value,
        frais: ligne?.[PLAN_DE_COM_LEGACY_FEE_FIELDS[index]] ?? '',
      }, index, ligne);
    })
    .filter(Boolean);
}

export function getPlanDeComLigneSupports(ligne) {
  return getPlanDeComLigneSupportItems(ligne);
}

export function getPlanDeComLigneSupportGroups(ligne) {
  const items = getPlanDeComLigneSupportItems(ligne);
  const groups = [];
  const indexByKey = new Map();

  items.forEach((item) => {
    const key = item.repeatable ? `qty:${item.tarifLibelle}` : `one:${item.index}`;
    if (indexByKey.has(key)) {
      const group = groups[indexByKey.get(key)];
      group.items.push(item);
      group.qty += 1;
      group.remises = [...group.remises, ...(item.remises || [])];
      return;
    }
    indexByKey.set(key, groups.length);
    groups.push({
      ...item,
      key,
      qty: 1,
      items: [item],
      displayLabel: item.label,
    });
  });

  return groups.map((group) => {
    const seen = new Set();
    const remises = [];
    for (const remise of group.remises || []) {
      const key = `${remise.key}:${remise.label}`;
      if (seen.has(key)) continue;
      seen.add(key);
      remises.push(remise);
    }
    return {
      ...group,
      remises,
      displayLabel: formatPlanDeComQuantityLabel(group.label, group.qty),
    };
  });
}

export function syncLigneSupports(ligne, items = []) {
  const cleaned = (items ?? [])
    .map((item) => {
      const value = String(item?.value ?? '').trim();
      if (!value) return null;
      const frais = String(item?.frais ?? item?.feeLabel ?? '').trim();
      return { value, frais: frais || null };
    })
    .filter(Boolean);

  const next = { ...ligne, supports: cleaned };
  PLAN_DE_COM_LEGACY_FORMAT_FIELDS.forEach((field, index) => {
    next[field] = cleaned[index]?.value ?? null;
    next[PLAN_DE_COM_LEGACY_FEE_FIELDS[index]] = cleaned[index]?.frais ?? null;
  });
  return next;
}

export function isPlanDeComLigneFilled(ligne) {
  return getPlanDeComLigneSupportItems(ligne).length > 0;
}

function moisAnneeToAbsoluteIndex(mois, annee) {
  const monthIndex = MOIS_LABELS.indexOf(String(mois ?? '').trim().toUpperCase());
  const year = Number(annee);
  if (monthIndex < 0 || !Number.isFinite(year)) return null;
  return year * 12 + monthIndex;
}

function absoluteIndexToMoisAnnee(absoluteIndex) {
  const year = Math.floor(absoluteIndex / 12);
  const monthIndex = ((absoluteIndex % 12) + 12) % 12;
  return {
    mois: MOIS_LABELS[monthIndex],
    annee: year,
  };
}

function cloneSupportItems(supports) {
  if (!Array.isArray(supports)) return [];
  return supports.map((item) => ({
    value: item?.value ?? item ?? null,
    frais: item?.frais ?? null,
  }));
}

function pickPlanDeComLigneContent(ligne) {
  const supportItems = getPlanDeComLigneSupportItems(ligne).map((item) => ({
    value: item.value,
    frais: item.feeLabel || null,
  }));
  const synced = syncLigneSupports(ligne, supportItems);

  return {
    mois: ligne?.mois ?? null,
    annee: ligne?.annee ?? null,
    type_campagne: ligne?.type_campagne ?? null,
    format_1: synced.format_1 ?? null,
    format_2: synced.format_2 ?? null,
    format_3: synced.format_3 ?? null,
    frais_crea: synced.frais_crea ?? null,
    frais_redac: synced.frais_redac ?? null,
    frais_klub: synced.frais_klub ?? null,
    supports: cloneSupportItems(synced.supports),
    calcules: ligne?.calcules ?? {},
  };
}

function emptyPlanDeComLigneContent(mois, annee) {
  return {
    mois,
    annee,
    type_campagne: null,
    format_1: null,
    format_2: null,
    format_3: null,
    frais_crea: null,
    frais_redac: null,
    frais_klub: null,
    supports: [],
    calcules: {},
  };
}

/**
 * Décale la fenêtre de 12 mois sans remapper les supports :
 * les campagnes restent sur leur mois calendaire, on retire des mois vides
 * en tête (début plus tard) ou en queue (début plus tôt).
 *
 * @returns {{
 *   ok: boolean,
 *   shifted?: boolean,
 *   error?: string,
 *   lignes?: object[],
 *   annee_debut?: number,
 *   annee_fin?: number,
 * }}
 */
export function shiftPlanDeComCalendarWindow(lignes, targetMois, targetAnnee) {
  const sorted = [...(lignes ?? [])].sort((a, b) => Number(a.ordre) - Number(b.ordre));
  if (sorted.length !== 12) {
    return { ok: false, error: 'Le calendrier doit contenir 12 mois.' };
  }

  const from = moisAnneeToAbsoluteIndex(sorted[0]?.mois, sorted[0]?.annee);
  const to = moisAnneeToAbsoluteIndex(targetMois, targetAnnee);
  if (from == null || to == null) {
    return { ok: false, error: 'Mois ou année invalide.' };
  }

  const delta = to - from;
  if (delta === 0) {
    return {
      ok: true,
      shifted: false,
      lignes: sorted,
      annee_debut: sorted[0].annee,
      annee_fin: sorted[sorted.length - 1].annee,
    };
  }

  if (Math.abs(delta) >= 12) {
    return {
      ok: false,
      error: 'Décalage trop large : videz d’abord tout le calendrier pour repartir sur une autre année.',
    };
  }

  const contents = sorted.map(pickPlanDeComLigneContent);

  if (delta > 0) {
    for (let i = 0; i < delta; i += 1) {
      if (isPlanDeComLigneFilled(sorted[i])) {
        const label = `${monthTitle(sorted[i].mois)} ${sorted[i].annee}`;
        return {
          ok: false,
          error: `Videz d’abord ${label} pour décaler le début du calendrier.`,
        };
      }
    }

    const kept = contents.slice(delta);
    const lastKept = kept[kept.length - 1];
    const lastIndex = moisAnneeToAbsoluteIndex(lastKept.mois, lastKept.annee);
    if (lastIndex == null) {
      return { ok: false, error: 'Mois ou année invalide.' };
    }

    const empties = [];
    for (let i = 1; i <= delta; i += 1) {
      const period = absoluteIndexToMoisAnnee(lastIndex + i);
      empties.push(emptyPlanDeComLigneContent(period.mois, period.annee));
    }
    const nextContents = [...kept, ...empties];
    const next = sorted.map((shell, index) => ({
      ...shell,
      ...nextContents[index],
      ordre: shell.ordre,
    }));

    return {
      ok: true,
      shifted: true,
      lignes: next,
      annee_debut: next[0].annee,
      annee_fin: next[next.length - 1].annee,
    };
  }

  const dropCount = -delta;
  for (let i = 0; i < dropCount; i += 1) {
    const ligne = sorted[sorted.length - 1 - i];
    if (isPlanDeComLigneFilled(ligne)) {
      const label = `${monthTitle(ligne.mois)} ${ligne.annee}`;
      return {
        ok: false,
        error: `Videz d’abord ${label} (en fin de calendrier) pour commencer plus tôt.`,
      };
    }
  }

  const kept = contents.slice(0, contents.length - dropCount);
  const firstKept = kept[0];
  const firstIndex = moisAnneeToAbsoluteIndex(firstKept.mois, firstKept.annee);
  if (firstIndex == null) {
    return { ok: false, error: 'Mois ou année invalide.' };
  }

  const empties = [];
  for (let i = dropCount; i >= 1; i -= 1) {
    const period = absoluteIndexToMoisAnnee(firstIndex - i);
    empties.push(emptyPlanDeComLigneContent(period.mois, period.annee));
  }
  const nextContents = [...empties, ...kept];
  const next = sorted.map((shell, index) => ({
    ...shell,
    ...nextContents[index],
    ordre: shell.ordre,
  }));

  return {
    ok: true,
    shifted: true,
    lignes: next,
    annee_debut: next[0].annee,
    annee_fin: next[next.length - 1].annee,
  };
}

export function getPlanDeComYearSpan(lignes = []) {
  return formatPlanDeComPeriode(lignes) || null;
}

export function monthTitle(mois) {
  const raw = String(mois ?? '').trim();
  if (!raw) return 'Mois';
  return raw.charAt(0) + raw.slice(1).toLowerCase();
}

export function getPlanDeComLigneFraisTotal(ligne) {
  return Number(ligne?.calcules?.Z ?? 0)
    + Number(ligne?.calcules?.AB ?? 0)
    + Number(ligne?.calcules?.AD ?? 0)
    + Number(ligne?.calcules?.extra_frais ?? 0);
}

export function getPlanDeComLigneTotal(ligne) {
  return Number(ligne?.calcules?.X ?? 0) + getPlanDeComLigneFraisTotal(ligne);
}

export function hasPlanDeComClient(dossier) {
  return String(dossier?.entreprise_label ?? '').trim() !== '';
}

export function hasPlanDeComCommercial(dossier) {
  const id = dossier?.commercial_id;
  return id != null && String(id).trim() !== '';
}

export function canEditPlanDeComSupports(dossier) {
  return hasPlanDeComClient(dossier) && hasPlanDeComCommercial(dossier);
}

export function getPlanDeComSupportsLockMessage(dossier) {
  const missing = [];
  if (!hasPlanDeComClient(dossier)) missing.push('l’entreprise');
  if (!hasPlanDeComCommercial(dossier)) missing.push('le commercial');
  if (!missing.length) return null;
  if (missing.length === 2) {
    return 'Choisissez d’abord l’entreprise et le commercial pour ajouter des campagnes.';
  }
  return `Choisissez d’abord ${missing[0]} pour ajouter des campagnes.`;
}
